import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Crown, CreditCard, Loader2, UserCheck, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../api";
import { Reveal } from "../components/motion";
import { Badge } from "./admin/AdminLayout";

export default function Account() {
  const [params] = useSearchParams();
  const [member, setMember] = useState(null);
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | connect | ready
  const [busy, setBusy] = useState(false);

  const token = () => localStorage.getItem("gm_member_token");

  useEffect(() => {
    const t = params.get("token");
    if (t) localStorage.setItem("gm_member_token", t);
    if (!token()) {
      setState("connect");
      return;
    }
    axios.get(`${API}/me/premium`, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => { setData(r.data); setState("ready"); })
      .catch(() => { localStorage.removeItem("gm_member_token"); setState("connect"); });
    try {
      const payload = JSON.parse(atob(token().split(".")[1]));
      setMember(payload);
    } catch { /* ignore */ }
  }, [params]);

  const signIn = async () => {
    const res = await api.get("/auth/discord/member/url?state=account");
    window.location.href = res.data.url;
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const res = await api.post("/payments/portal", {}, { headers: { Authorization: `Bearer ${token()}` } });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the billing portal");
      setBusy(false);
    }
  };

  return (
    <main className="pt-16 min-h-screen" data-testid="account-page">
      <section className="hero-glow border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-volt font-semibold mb-5 flex items-center gap-3">
              <span className="w-8 h-px bg-volt inline-block" /> Account
            </p>
            <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tight" style={{ fontWeight: 900 }} data-testid="account-headline">
              MY SUBSCRIPTION
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
        {state === "loading" && <p className="text-white/40 font-mono text-sm">Loading…</p>}

        {state === "connect" && (
          <Reveal>
            <div className="max-w-lg rounded-xl border border-white/10 bg-white/[0.03] p-8 text-center" data-testid="account-connect">
              <Link2 size={28} className="mx-auto text-blurple mb-4" />
              <h2 className="font-heading text-xl font-bold text-white mb-2">Sign in to manage Premium</h2>
              <p className="text-sm text-muted-foreground mb-6">We use your Discord account to find your subscription — no passwords, no forms.</p>
              <button onClick={signIn} data-testid="account-discord-signin"
                className="inline-flex items-center gap-2 bg-blurple hover:bg-[#4752c4] text-white font-heading font-bold px-8 py-3.5 rounded-full transition-colors duration-200">
                <Link2 size={16} /> Sign in with Discord
              </button>
            </div>
          </Reveal>
        )}

        {state === "ready" && data && (
          <Reveal>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-8" data-testid="account-status">
              <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <UserCheck size={20} className="text-emerald-400" />
                  <p className="font-heading font-bold text-white text-lg">{member?.name}</p>
                </div>
                <Badge tone={data.premium ? "green" : "grey"}>{data.premium ? "Premium active" : "Free tier"}</Badge>
              </div>

              {data.subscriptions.length > 0 ? (
                <div className="space-y-3 mb-8" data-testid="account-subscriptions">
                  {data.subscriptions.map((s) => (
                    <div key={s.id} className="flex items-center justify-between rounded-lg border border-white/5 bg-black/30 px-5 py-4 flex-wrap gap-3">
                      <div>
                        <p className="text-sm font-semibold text-white">{s.plan} {s.guild_id ? "· whole server" : "· personal"}</p>
                        <p className="text-xs text-white/40 font-mono mt-0.5">since {new Date(s.since).toLocaleDateString()} · ${s.mrr}/mo · {s.provider}</p>
                      </div>
                      <Badge tone={s.status === "active" ? "green" : s.status === "cancelled" ? "grey" : "amber"}>{s.status}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mb-8">No subscription found for this Discord account yet.</p>
              )}

              <div className="flex gap-3 flex-wrap">
                {data.subscriptions.some((s) => s.status === "active") ? (
                  <button onClick={openPortal} disabled={busy} data-testid="account-portal-button"
                    className="inline-flex items-center gap-2 bg-blurple hover:bg-[#4752c4] disabled:opacity-50 text-white font-heading font-bold px-7 py-3 rounded-full transition-colors duration-200">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />}
                    Manage / cancel subscription
                  </button>
                ) : (
                  <Link to="/premium" data-testid="account-upgrade-link"
                    className="inline-flex items-center gap-2 bg-blurple hover:bg-[#4752c4] text-white font-heading font-bold px-7 py-3 rounded-full transition-colors duration-200">
                    <Crown size={15} /> Get Premium
                  </Link>
                )}
              </div>
              <p className="text-xs text-white/30 mt-5 font-mono">Cancellation, payment method and invoices are handled by Stripe's secure portal.</p>
            </div>
          </Reveal>
        )}
      </section>
    </main>
  );
}
